import { IconType } from 'react-icons'
import { LuCalendar1, LuCalendarDays, LuCalendarRange, LuCalendarSearch } from 'react-icons/lu'

export const INTERVIEW_SLOT_SELECTION_DROPDOWN_LABEL = 'Share slots with candidate'

export const MIN_INTERVIEW_SLOT_SELECTION_COUNT = 3

export const INTERVIEW_SLOT_SELECTION_TYPES = {
    CHOOSE_SLOT_MANUALLY: 'choose_slot_manually',
    RECRUITER_MINIMUM_SLOTS: 'recruiter_minimum_slots',
    SEND_ALL_AVAILABLE_SLOTS: 'send_all_available_slots',
    AUTOMATIC_SLOT_GENERATION: 'automatic_slot_generation',
} as const

export type InterviewSlotSelectionType = (typeof INTERVIEW_SLOT_SELECTION_TYPES)[keyof typeof INTERVIEW_SLOT_SELECTION_TYPES]

export type SlotSelectionOption = {
    value: InterviewSlotSelectionType
    title: string
    subtitle: string
    icon: IconType
}

type SlotSelectionOptionMeta = {
    title: string
    subtitle: string
    icon: IconType
}

const SLOT_SELECTION_OPTION_META: Record<InterviewSlotSelectionType, SlotSelectionOptionMeta> = {
    choose_slot_manually: {
        title: 'Specific slot chosen by recruiter',
        subtitle: 'Book one fixed time slot',
        icon: LuCalendar1,
    },
    recruiter_minimum_slots: {
        title: 'Minimum 3 slots chosen by recruiter',
        subtitle: 'Recruiter picks ≥3 times; candidate chooses one',
        icon: LuCalendarSearch,
    },
    automatic_slot_generation: {
        title: 'Minimum 3 slots chosen by panelist(s)',
        subtitle: 'Panelists pick ≥3; candidate picks overlap',
        icon: LuCalendarDays,
    },
    send_all_available_slots: {
        title: 'All available slots within date range',
        subtitle: 'Candidate picks any slot in range',
        icon: LuCalendarRange,
    },
}

const USER_FACING_SLOT_SELECTION_ORDER: InterviewSlotSelectionType[] = [
    INTERVIEW_SLOT_SELECTION_TYPES.CHOOSE_SLOT_MANUALLY,
    INTERVIEW_SLOT_SELECTION_TYPES.SEND_ALL_AVAILABLE_SLOTS,
    INTERVIEW_SLOT_SELECTION_TYPES.RECRUITER_MINIMUM_SLOTS,
    INTERVIEW_SLOT_SELECTION_TYPES.AUTOMATIC_SLOT_GENERATION,
]

export function getSlotSelectionOptions(isSelfSchedulingEnabled?: boolean): SlotSelectionOption[] {
    const types = isSelfSchedulingEnabled
        ? USER_FACING_SLOT_SELECTION_ORDER
        : USER_FACING_SLOT_SELECTION_ORDER.filter((value) => value === INTERVIEW_SLOT_SELECTION_TYPES.CHOOSE_SLOT_MANUALLY)

    return types.map((value) => ({
        value,
        ...SLOT_SELECTION_OPTION_META[value],
    }))
}

export function getSlotSelectionOption(value: string): SlotSelectionOption | undefined {
    const meta = SLOT_SELECTION_OPTION_META[value as InterviewSlotSelectionType]
    if (!meta) return undefined
    return { value: value as InterviewSlotSelectionType, ...meta }
}

/** @deprecated Use getSlotSelectionOption(value)?.title */
export function getSlotSelectionOptionLabel(value: string): string | undefined {
    return getSlotSelectionOption(value)?.title
}

export function hasAssignedPanelists(interviewRound?: { PanellistFeedback?: { email?: string | null }[] } | null): boolean {
    return Boolean(interviewRound?.PanellistFeedback?.some((panelist) => Boolean(panelist?.email)))
}

/** Progress label for multi-slot pickers (e.g. recruiter curated / panelist). */
export function getSlotSelectionProgressLabel(selectedCount: number, minCount: number = MIN_INTERVIEW_SLOT_SELECTION_COUNT): string {
    if (selectedCount <= 0) return `Select at least ${minCount} slots`
    if (selectedCount < minCount) {
        const remaining = minCount - selectedCount
        return `Select ${remaining} more ${remaining === 1 ? 'slot' : 'slots'}`
    }
    return `${selectedCount} slots selected`
}

/** Tooltip when the multi-slot confirm action is disabled. */
export function getSlotSelectionProgressTooltip(selectedCount: number, minCount: number = MIN_INTERVIEW_SLOT_SELECTION_COUNT): string {
    if (selectedCount >= minCount) return ''
    if (selectedCount <= 0) return `Need to select ${minCount} slots to continue`
    const remaining = minCount - selectedCount
    return `Select ${remaining} more ${remaining === 1 ? 'slot' : 'slots'} to continue`
}
